import { Box, List, ListItem, makeStyles, Theme, Typography } from '@material-ui/core';
import axios from 'axios';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { member as v_member, basic, Values, product as v_product, SNACKBAR_TIME } from '../../../request/values';
import ToastStr from '../../../request/toastStr';
import { TitleBackBar } from '../../../shared/header/TitleBar';
import { COLORS } from '../../../shared/Theme';
import { DialogInputConfirm, DialogSelectCompany } from '../../../components/Dialog';
import { formatMoneyAmount, getTranslatedLicenseName, formatToTimezone } from '../../../locales/formatModule';

const useStyles = makeStyles((theme: Theme) => ({
	itemProcessing: {
		backgroundColor: `${COLORS.LIGHT_GREY}`,
	},
	list: {
		width: '86vw',
		height: '74.5vh',
		overflowY: 'auto',
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		...theme.typography.h3,
		color: `${COLORS.BROWNISH_GREY}`,
		padding: '4px',
		[theme.breakpoints.up('md')]: {
			width: '100%',
			height: '100%',
			paddingTop: theme.spacing(3),
			paddingBottom: theme.spacing(6),
		},
	},
	productItem: {
		width: 'auto',
		height: 'fit-content',
		borderRadius: 5,
		marginBottom: '1.7vh',
		margin: 0,
		padding: '20px',
		boxSizing: 'border-box',
		'& #itemWrap': {
			width: '100%',
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: '10px',
			'& #productImage': {
				flex: 1,
				'& #productImageSize': {
					width: '100%',
				},
			},
			'& #productDesc': {
				display: 'flex',
				flexDirection: 'column',
				flex: 2,
				gap: '12px',
				'& #productInfo': {
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
					fontSize: '14px',
					lineHeight: '20px',
					'& #productLevel': {
						backgroundColor: `${COLORS.WATER_BLUE}`,
						color: `white`,
						padding: '3px 10px',
						borderRadius: '10px',
						'&.complete': {
							backgroundColor: `#e5e7eb`,
							color: `black`,
						},
					},
					'& #productStatus': {
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'center',
						alignItems: 'center',
						backgroundColor: `${COLORS.WATER_BLUE}`,
						color: `white`,
						padding: '3px 10px',
						borderRadius: '10px',
						gap: '5px',
						'&.complete': {
							backgroundColor: `#e5e7eb`,
							color: `black`,
						},
						'& #productStatusImage': {
							width: '16px',
							height: '16px',
						},
					},
				},
				'& #productName': {
					fontSize: '18px',
					lineHeight: '24px',
					color: 'black',
					fontWeight: 'bold',
				},
				'& #productPrice': {
					fontSize: '24px',
					lineHeight: '32px',
					color: `${COLORS.WATER_BLUE}`,
					fontWeight: 'bold',
				},
				'& #productSaleInfo': {
					display: 'flex',
					flexDirection: 'column',
					gap: '4px',
					fontSize: '12px',
					lineHeight: '16px',
					color: `${COLORS.BROWNISH_GREY}`,
				},
				'& #productEditButton': {
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '12px',
					backgroundColor: `${COLORS.WATER_BLUE}`,
					color: `white`,
					padding: '10px 10px',
					borderRadius: '10px',
					fontSize: '14px',
					fontWeight: 'bold',
					lineHeight: '18px',
					border: 'none',
					'& #productEditImage': {
						width: '20px',
						height: '20px',
					},
				},
			},
		},
	},
	empty: {
		height: '62vh',
		textAlign: 'center',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& img': {
			width: '35vw',
			height: '35vw',
			[theme.breakpoints.up('md')]: {
				width: '25vw',
				height: '25vw',
			},
			marginBottom: '2.5vh',
			opacity: 0.4,
		},
		'& h1': {
			color: `${COLORS.DOWN}`,
		},
	},
	productDesc: {
		paddingLeft: '5vw',
		flexGrow: 1,
		'& #productName': {
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			...theme.typography.body1,
			color: `${COLORS.BROWNISH_GREY}`,
		},
		'& #productPrice': {
			...theme.typography.subtitle2,
			color: `${COLORS.CHARCOAL_GREY}`,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
		},
	},
	timeText: {
		...theme.typography.h1,
		fontFamily: 'NanumR',
		fontWeight: 'normal',
		[theme.breakpoints.up('md')]: {
			...theme.typography.h5,
			fontFamily: 'NanumR',
			fontWeight: 'normal',
		},
	},
	paper: {
		padding: theme.spacing(1),
		'& #itemWrap': {
			width: '100%',
			display: 'flex',
			padding: '0',
			justifyContent: 'center',
			// alignItem: 'center',
		},
	},
	grid: {
		marginTop: theme.spacing(1),
	},
}));

interface iCompanyInfo {
	companyNum: number;
	companyName: string;
}

const ProductSaleList: React.FC<any> = (props) => {
	const classes = useStyles();
	const history = useHistory();
	const { member } = props;
	const { t } = useTranslation('student');

	const [product, setProduct] = useState<any>([]);
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const [priceDialog, setPriceDialog] = useState(-1);
	const [companyDialog, setCompanyDialog] = useState(-1);
	const [companyList, setCompanyList] = useState<iCompanyInfo[]>([]);

	const count = 20;
	let max_count: number = 999;
	const loader = useRef(null);
	const [page, setPage] = useState(-count);

	const handleObserver = useCallback((entries: any) => {
		const target = entries[0];
		if (target.isIntersecting) {
			setPage((prevState) => {
				if (prevState + count > max_count) {
					return prevState;
				}
				return prevState + count;
			});
		}
	}, []);

	const handleDialogOpen = (index: number, key: string) => {
		if (priceDialog === -1 && companyDialog === -1) {
			if (key === 'v4') {
				setPriceDialog(index);
			} else {
				setCompanyDialog(index);
			}
		}
	};

	const handleDialogClose = (key: string) => {
		if (priceDialog === -1 && companyDialog === -1) {
			return;
		}
		if (key === 'v4') {
			setPriceDialog((prevState) => -1);
		} else {
			setCompanyDialog((prevState) => -1);
		}
	};

	const getDialogContext = () => {
		const tProduct: any = product[priceDialog];
		const minPrice: number = tProduct.productPrice * (9 / 10);
		const maxPrice: number = tProduct.productPrice * (11 / 10);

		return `${t('ProductSaleList.minMaxContext', { min: formatMoneyAmount(Math.floor(minPrice)), max: formatMoneyAmount(Math.floor(maxPrice)) })}`;
	};

	const getCompanyList = () => {
		let url = new URL(basic.server + v_member.member + v_member.decompose_company_list);
		let params = { classCode: member.classCode };
		url.search = new URLSearchParams(params).toString();

		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code == Values.SUCCESS_CODE) {
					setCompanyList(data.companyList);
					console.log('company list? ', data.companyList);
				}
			});
	};

	const getProductOrderList = (position: number, count: number) => {
		let url = new URL(basic.server + v_member.member + v_member.product_order_list);
		let params = {
			companyNum: member.companyNum,
			position: position.toString(),
			count: count.toString(),
		};
		url.search = new URLSearchParams(params).toString();
		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);

				if (data.code === Values.SUCCESS_CODE) {
					if (product.length === 0 && data.products.length === 0) {
						enqueueSnackbar(ToastStr.MATERIAL_ORDER_LIST_EMPTY_STR, {
							variant: 'success',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else if (data.products === null) {
						enqueueSnackbar(ToastStr.MATERIAL_ORDER_LIST_EMPTY_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else {
						console.log(data.products);
						if (product.length === 0) {
							setProduct((prevState: any) => data.products);
						} else {
							setProduct((prevState: any) => prevState.concat(data.products));
						}
					}
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});
	};

	const postProductSaleCompany = (companyNum: number) => {
		if (companyNum === undefined || companyNum === 0) {
			enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_NO_COMPANY_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		const targetProduct: any = product[companyDialog];
		console.log('target? ', targetProduct);
		axios({
			method: 'POST',
			data: {
				productSaleAppNum: targetProduct.productSaleAppNum.toString(),
				companyNum: companyNum,
			},
			withCredentials: true,
			url: basic.server + v_member.member + v_product.sale_company_update,
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_COMPANY_UPDATE_STR, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.PRODUCT_SALE_COMPLETE_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_FINISH, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});

		handleDialogClose('v1');
	};

	const postProductSaleApplication = (price: number, companyNum: number) => {
		const minPrice: number = Math.floor(product[priceDialog].productPrice * (9 / 10));
		const maxPrice: number = Math.floor(product[priceDialog].productPrice * (11 / 10));
		if (price < minPrice || price > maxPrice) {
			enqueueSnackbar(ToastStr.PRODUCT_PRICE_WRONG_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		if (companyNum === undefined || companyNum === 0) {
			enqueueSnackbar(ToastStr.PRODUCT_SALE_APP_NO_COMPANY_STR, {
				variant: 'warning',
				autoHideDuration: SNACKBAR_TIME,
			});
			return;
		}
		const targetProduct: any = product[priceDialog];
		axios({
			method: 'POST',
			data: {
				productSaleAppNum: targetProduct.productSaleAppNum.toString(),
				price: price.toString(),
				decomposeCompanyNum: companyNum,
			},
			withCredentials: true,
			url: basic.server + v_member.member + v_product.sale_application_update,
		})
			.then((res) => res.data)
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					const updatedProducts = [...product];
					updatedProducts[priceDialog] = {
						...updatedProducts[priceDialog],
						productSalePrice: price,
					};
					setProduct(updatedProducts);
					enqueueSnackbar(ToastStr.PRODUCT_SALE_UPDATE_SUCCESS, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.PRODUCT_SALE_COMPLETE_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_SALE_FINISH, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});
		handleDialogClose('v4');
	};

	useEffect(() => {
		const option = {
			root: null,
			rootMargin: '20px',
			threshold: 0,
		};
		const observer = new IntersectionObserver(handleObserver, option);
		if (loader.current) observer.observe(loader.current!);
	}, [handleObserver]);

	useEffect(() => {
		//console.log(page, count)
		if (page >= 0) {
			getProductOrderList(page, count);
		}
	}, [page]);

	useEffect(() => {
		getCompanyList();
	}, []);

	return (
		<div className="content">
			<TitleBackBar history={history} title={t('ProductSaleList.pageTitle')} titleIcon="order" />
			<Box display="grid" justifyContent="center" alignItems="center">
				<List className={classes.list}>
					{product.length !== 0 ? (
						product.map((item: any, index: number) => {
							return (
								<ListItem
									className={classes.productItem}
									style={{
										border: item.productSaleStatus === 'complete' ? 'solid 1px #e5e7eb' : 'solid 1px #1291d9',
									}}
									alignItems="center"
									key={index}
								>
									<ProductItem
										index={index}
										{...item}
										handleButton={() => (member.classVersion === 4 || member.classVersion === 5 ? handleDialogOpen(index, 'v4') : handleDialogOpen(index, 'v1'))}
										member={member}
									/>
								</ListItem>
							);
						})
					) : (
						<div className={classes.empty}>
							<div>
								<img src="/images/title/order.webp" />
								<Typography variant="h1">{t('ProductSaleList.empty')}</Typography>
							</div>
						</div>
					)}
					<div ref={loader} />
				</List>
			</Box>
			{priceDialog >= 0 && (
				<DialogInputConfirm
					open={priceDialog < 0 ? false : true}
					handleClose={() => handleDialogClose('v4')}
					title={t('ProductSaleList.dialogPriceTitle')}
					companyList={companyList}
					price={product[priceDialog].productSalePrice}
					context={getDialogContext()}
					yesConfirm={postProductSaleApplication}
					noConfirm={() => handleDialogClose('v4')}
				/>
			)}
			{companyDialog >= 0 && (
				<DialogSelectCompany
					open={companyDialog < 0 ? false : true}
					handleClose={() => {
						handleDialogClose('');
					}}
					title={t('ProductSaleList.dialogSelectCompanyTitle')}
					companyList={companyList}
					yesConfirm={postProductSaleCompany}
					noConfirm={() => {
						handleDialogClose('');
					}}
				/>
			)}
		</div>
	);
};

const ProductItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const licenseName = getTranslatedLicenseName(tObject, props.productName);
	const formattedTimezone = formatToTimezone(props.productSaleAppDate);
	const formattedDate = formattedTimezone ? formattedTimezone[0] : '';
	const formattedTime = formattedTimezone ? formattedTimezone[1] : '';
	return (
		<div id="itemWrap">
			<div id="productImage">
				<img id="productImageSize" src={basic.server + '/displayImage?fileName=' + props.productImage} />
			</div>
			<div id="productDesc">
				<div id="productInfo">
					<div id="productLevel" className={props.productSaleStatus === 'complete' ? 'complete' : ''}>{`Lv. ${props.productLicenseLevel}`}</div>
					<div id="productStatus" className={props.productSaleStatus === 'complete' ? 'complete' : ''}>
						<img id="productStatusImage" src={props.productSaleStatus === 'complete' ? '/images/circle_check_black.svg' : '/images/clock_white.svg'} />
						<div id="productStatusText" data-testid={`manu-product-sale-status-text-${props.productLicenseLevel}`}>
							{props.productSaleStatus === 'complete' ? t('ProductSaleList.statusComplete') : t('ProductSaleList.statusProcessing')}
						</div>
					</div>
				</div>
				<Typography id="productName">{licenseName}</Typography>
				<Typography id="productPrice" data-testid={`manu-product-sale-price-${props.productLicenseLevel}`}>
					{formatMoneyAmount(props.productSalePrice)}
				</Typography>
				{props.productSaleStatus === 'complete' ? (
					<div id="productSaleInfo">
						<div>{`${t('ProductSaleList.buyerCompanyPrefix')} ${props.companyName}`}</div>
						<div>{formattedTime}</div>
					</div>
				) : (
					<button id="productEditButton" onClick={props.handleButton} data-testid={`manu-product-sell-edit-btn-${props.productLicenseLevel}`}>
						<img id="productEditImage" src="/images/edit_white.svg" />
						<div id="productEditText">{t('ProductSaleList.editTradeInfo')}</div>
					</button>
				)}
			</div>
		</div>
	);
};
export default ProductSaleList;
